/**
 * CollabInbox — wiederverwendbare Inbox-Komponente für SB und FL.
 *
 * Zwei Modi:
 *   - mode="freelancer": holt eigene Threads, filtert über advisor automatisch
 *   - mode="advisor":    holt Threads für einen bestimmten client_id
 *
 * Two-Pane Layout: Thread-Liste links, Thread-Detail rechts.
 * Mobile: Liste zeigt; bei Klick wird Detail als Overlay angezeigt.
 *
 * API:
 *   GET    /api/collab/threads?client_id=&filter=
 *   GET    /api/collab/threads/{id}
 *   POST   /api/collab/threads/{id}/messages
 *   PATCH  /api/collab/threads/{id}
 *   POST   /api/collab/threads
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import TextTemplatePopover from '@/components/advisor/TextTemplatePopover';
import {
  MessageCircle, CheckSquare, FileQuestion, StickyNote, Search,
  Send, Paperclip, ArrowLeft, Inbox as InboxIcon, Plus, X,
  AlertCircle, BellOff, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = { fontFamily: 'Manrope, sans-serif' };

const TYPE_META = {
  question: { Icon: MessageCircle, label: 'Frage' },
  task: { Icon: CheckSquare, label: 'Aufgabe' },
  request: { Icon: FileQuestion, label: 'Dokumenten-Anfrage' },
  note: { Icon: StickyNote, label: 'Notiz' },
};

function getHeaders(authMode) {
  const tokenKey = authMode === 'advisor' ? 'advisor_token' : 'auth_token';
  const token = localStorage.getItem(tokenKey);
  return { Authorization: `Bearer ${token}` };
}

function formatAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'jetzt';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  if (diff < 604800) return `vor ${Math.floor(diff / 86400)} Tagen`;
  return d.toLocaleDateString('de-DE');
}

function TypeIcon({ type, className = 'h-4 w-4' }) {
  const meta = TYPE_META[type] || TYPE_META.question;
  const Icon = meta.Icon;
  return <Icon className={className} />;
}

const FILTERS_FOR = {
  freelancer: [
    { key: 'mine', label: 'Wartet auf mich' },
    { key: 'waiting', label: 'Wartet auf SB' },
    { key: 'snoozed', label: 'Pausiert' },
    { key: 'done', label: 'Erledigt' },
    { key: 'all', label: 'Alle' },
  ],
  advisor: [
    { key: 'mine', label: 'Wartet auf mich' },
    { key: 'waiting', label: 'Wartet auf Mandant' },
    { key: 'snoozed', label: 'Pausiert' },
    { key: 'done', label: 'Erledigt' },
    { key: 'all', label: 'Alle' },
  ],
};

// Snooze options shown in dropdown
const SNOOZE_OPTIONS = [
  { hours: 3, label: '3 Stunden' },
  { hours: 24, label: 'Morgen' },
  { hours: 24 * 3, label: 'in 3 Tagen' },
  { hours: 24 * 7, label: 'nächste Woche' },
];

export default function CollabInbox({
  mode, // 'freelancer' | 'advisor'
  clientId, // required for both modes (FL passes own id; SB passes mandant id)
  onCountsChange, // optional callback for parent to update badges
  initialFilter = 'mine',
}) {
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState('');
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // { thread, messages }
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCompose, setShowCompose] = useState(false);

  const authMode = mode === 'advisor' ? 'advisor' : 'freelancer';

  const fetchThreads = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/collab/threads`, {
        params: { client_id: clientId, filter },
        headers: getHeaders(authMode),
      });
      const list = res.data.threads || [];
      // Defensive de-dup by id (in case backend ever returns duplicates from migration)
      const seen = new Set();
      const deduped = list.filter((t) => {
        if (seen.has(t.id)) return false;
        seen.add(t.id);
        return true;
      });
      setThreads(deduped);
      if (onCountsChange) onCountsChange(deduped);
      // Auto-select first if nothing selected
      if (deduped.length > 0 && (!selectedId || !deduped.find((t) => t.id === selectedId))) {
        setSelectedId(deduped[0].id);
      } else if (deduped.length === 0) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (err) {
      if (err.response?.status === 404) setThreads([]);
      else toast.error('Vorgänge konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [clientId, filter, authMode, onCountsChange, selectedId]);

  useEffect(() => { fetchThreads(); }, [clientId, filter, authMode]); // eslint-disable-line

  const fetchDetail = useCallback(async () => {
    if (!selectedId) { setDetail(null); return; }
    try {
      const res = await axios.get(`${API}/collab/threads/${selectedId}`, { headers: getHeaders(authMode) });
      setDetail(res.data);
    } catch (err) {
      // Silent fallback for 404 (e.g., stale id, migration inconsistency)
      if (err.response?.status === 404) {
        setDetail({ thread: null, messages: [], notFound: true });
      } else {
        toast.error('Details konnten nicht geladen werden.');
        setDetail(null);
      }
    }
  }, [selectedId, authMode]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const sendReply = async () => {
    if (!reply.trim() || !selectedId) return;
    try {
      await axios.post(`${API}/collab/threads/${selectedId}/messages`,
        { body: reply.trim() },
        { headers: getHeaders(authMode) });
      setReply('');
      await Promise.all([fetchThreads(), fetchDetail()]);
    } catch (err) {
      toast.error('Antwort konnte nicht gesendet werden.');
    }
  };

  const markDone = async () => {
    if (!selectedId) return;
    try {
      await axios.patch(`${API}/collab/threads/${selectedId}`, { status: 'done' }, { headers: getHeaders(authMode) });
      toast.success('Als erledigt markiert.');
      await Promise.all([fetchThreads(), fetchDetail()]);
    } catch {
      toast.error('Status konnte nicht geändert werden.');
    }
  };

  const archive = async () => {
    if (!selectedId) return;
    try {
      await axios.patch(`${API}/collab/threads/${selectedId}`, { is_archived: true }, { headers: getHeaders(authMode) });
      toast.success('Archiviert.');
      setSelectedId(null);
      await fetchThreads();
    } catch {
      toast.error('Konnte nicht archiviert werden.');
    }
  };

  const snooze = async (hours) => {
    if (!selectedId) return;
    const target = new Date(Date.now() + hours * 3600 * 1000).toISOString();
    try {
      await axios.patch(`${API}/collab/threads/${selectedId}`,
        { snoozed_until: target },
        { headers: getHeaders(authMode) });
      toast.success(`Pausiert bis ${new Date(target).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}`);
      setSelectedId(null);
      await fetchThreads();
    } catch {
      toast.error('Pausieren fehlgeschlagen.');
    }
  };

  const unsnooze = async () => {
    if (!selectedId) return;
    try {
      await axios.patch(`${API}/collab/threads/${selectedId}`, { snoozed_until: '' }, { headers: getHeaders(authMode) });
      toast.success('Wieder aktiv.');
      await Promise.all([fetchThreads(), fetchDetail()]);
    } catch {
      toast.error('Aktivieren fehlgeschlagen.');
    }
  };

  const filtered = threads.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (t.subject || '').toLowerCase().includes(q) || (t.client_name || '').toLowerCase().includes(q);
  });

  return (
    <Card className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm" data-testid="collab-inbox">
      <div className="flex min-h-[480px] sm:min-h-[560px]">
        {/* List */}
        <div className={`${selectedId ? 'hidden sm:flex' : 'flex'} w-full sm:max-w-sm flex-col border-r border-slate-200 bg-slate-50`}>
          <div className="border-b border-slate-200 bg-white px-4 py-3">
            <div className="mb-3 flex flex-wrap gap-1">
              {FILTERS_FOR[mode].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                    filter === f.key
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Suchen…"
                  className="pl-9"
                  data-testid="inbox-search"
                />
              </div>
              <Button size="sm" onClick={() => setShowCompose(true)} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="new-thread-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-emerald-600" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <InboxIcon className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                <p className="text-sm font-medium text-slate-900">Keine Vorgänge</p>
                <p className="mt-1 text-xs text-slate-500">Im aktuellen Filter sind keine Einträge vorhanden.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filtered.map((t) => {
                  const isMyTurn = t.owner_type === (mode === 'advisor' ? 'advisor' : 'freelancer') && t.status === 'open';
                  const isSelected = selectedId === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setSelectedId(t.id)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                        isSelected ? 'bg-white' : 'hover:bg-white/60'
                      }`}
                      data-testid={`thread-row-${t.id}`}
                    >
                      <div className={`flex-shrink-0 rounded-lg p-2 ${isSelected ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                        <TypeIcon type={t.thread_type} className={`h-4 w-4 ${isSelected ? 'text-emerald-600' : 'text-slate-500'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`truncate text-sm ${isMyTurn ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                            {t.subject}
                          </p>
                          {t.priority === 'urgent' && (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 text-[10px] font-bold text-amber-700">!</span>
                          )}
                          {t.snoozed_until && new Date(t.snoozed_until) > new Date() && (
                            <Clock className="h-3 w-3 text-blue-500" title="Pausiert" />
                          )}
                          {t.status === 'done' && (
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 text-[10px] font-medium text-emerald-700">erledigt</span>
                          )}
                        </div>
                        {mode === 'advisor' && (
                          <p className="mt-0.5 truncate text-xs text-slate-500">{t.client_name}</p>
                        )}
                        <div className="mt-1 flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">
                            {isMyTurn ? 'Du bist dran' : t.status === 'done' ? '—' : (mode === 'advisor' ? 'Mandant ist dran' : 'SB ist dran')}
                          </span>
                          <span className="font-mono text-[10px] text-slate-400">{formatAgo(t.last_message_at)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Detail */}
        <div className={`${selectedId ? 'flex' : 'hidden sm:flex'} flex-1 flex-col bg-white`}>
          {!detail || !detail.thread ? (
            <div className="flex flex-1 items-center justify-center px-6">
              <div className="text-center">
                <InboxIcon className="mx-auto mb-2 h-10 w-10 text-slate-300" />
                <p className="text-sm font-medium text-slate-700">
                  {detail?.notFound ? 'Vorgang nicht mehr verfügbar' : 'Vorgang auswählen'}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {detail?.notFound
                    ? 'Dieser Eintrag wurde archiviert oder gelöscht. Bitte aktualisiere die Liste.'
                    : 'Wähle links einen Vorgang, um die Konversation zu sehen.'}
                </p>
                {detail?.notFound && (
                  <Button size="sm" variant="outline" onClick={() => { setSelectedId(null); fetchThreads(); }} className="mt-3">
                    Liste neu laden
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 sm:px-6 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSelectedId(null)} className="sm:hidden rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Zurück">
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <div className="rounded-lg bg-emerald-50 p-1.5">
                      <TypeIcon type={detail.thread.thread_type} className="h-4 w-4 text-emerald-600" />
                    </div>
                    <h3 className="truncate text-base font-semibold text-slate-900" style={H} data-testid="thread-detail-subject">{detail.thread.subject}</h3>
                  </div>
                  <p className="mt-1 ml-8 sm:ml-0 text-xs text-slate-500">
                    {TYPE_META[detail.thread.thread_type]?.label || 'Vorgang'} ·{' '}
                    {mode === 'advisor' ? detail.thread.client_name : detail.thread.advisor_name} · {formatAgo(detail.thread.last_message_at)}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {detail.thread.snoozed_until && new Date(detail.thread.snoozed_until) > new Date() ? (
                    <Button size="sm" variant="outline" onClick={unsnooze} data-testid="unsnooze-btn">
                      <BellOff className="mr-1 h-3 w-3" /> Pause beenden
                    </Button>
                  ) : (
                    detail.thread.status !== 'done' && (
                      <SnoozeMenu onSnooze={snooze} />
                    )
                  )}
                  {detail.thread.status !== 'done' && (
                    <Button size="sm" variant="outline" onClick={markDone} data-testid="mark-done-btn">
                      <CheckSquare className="mr-1 h-3 w-3" /> Erledigt
                    </Button>
                  )}
                  <button onClick={archive} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Archivieren" data-testid="archive-btn">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex-1 space-y-5 overflow-y-auto bg-slate-50 p-4 sm:p-6" data-testid="messages-list">
                {detail.thread.snoozed_until && new Date(detail.thread.snoozed_until) > new Date() && (
                  <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    <Clock className="h-3.5 w-3.5" />
                    Pausiert bis {new Date(detail.thread.snoozed_until).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                  </div>
                )}
                {(detail.messages || []).map((m) => (
                  <div key={m.id} className="flex gap-3">
                    <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full font-mono text-xs font-semibold ${
                      m.author_type === 'advisor' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {m.author_type === 'advisor' ? 'SB' : 'FL'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-baseline gap-2 flex-wrap">
                        <p className="text-sm font-medium text-slate-900">{m.author_name || (m.author_type === 'advisor' ? 'Steuerberater' : 'Freelancer')}</p>
                        <p className="font-mono text-[11px] text-slate-400">{formatAgo(m.created_at)}</p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                        {m.body}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {detail.thread.status !== 'done' && !detail.thread.is_archived && (
                <div className="border-t border-slate-200 bg-white p-3 sm:p-4">
                  <div className="rounded-xl border border-slate-200 bg-white">
                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      rows="2"
                      placeholder="Antwort schreiben…"
                      className="border-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                      data-testid="reply-input"
                    />
                    <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <button className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Datei anhängen (in Kürze)" disabled>
                          <Paperclip className="h-4 w-4" />
                        </button>
                        {mode === 'advisor' && (
                          <TextTemplatePopover
                            currentValue={reply}
                            onSelect={(text) => setReply((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))}
                          />
                        )}
                      </div>
                      <Button size="sm" onClick={sendReply} disabled={!reply.trim()} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="send-reply-btn">
                        <Send className="mr-1 h-3 w-3" /> Senden
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {detail.thread.status === 'done' && (
                <div className="border-t border-slate-200 bg-emerald-50 px-4 sm:px-6 py-3 text-xs text-emerald-700 flex items-center gap-2">
                  <CheckSquare className="h-3.5 w-3.5" />
                  Erledigt am {detail.thread.closed_at ? new Date(detail.thread.closed_at).toLocaleString('de-DE') : '—'}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <ComposeThreadModal
          mode={mode}
          clientId={clientId}
          onClose={() => setShowCompose(false)}
          onCreated={async () => { setShowCompose(false); await fetchThreads(); }}
        />
      )}
    </Card>
  );
}

// ────────── Snooze Menu Dropdown ──────────
function SnoozeMenu({ onSnooze }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} data-testid="snooze-btn">
        <BellOff className="mr-1 h-3 w-3" /> Pausieren
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg" data-testid="snooze-menu">
            {SNOOZE_OPTIONS.map((opt) => (
              <button
                key={opt.hours}
                onClick={() => { onSnooze(opt.hours); setOpen(false); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                data-testid={`snooze-${opt.hours}h`}
              >
                <Clock className="h-3.5 w-3.5 text-slate-400" />
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ────────── Compose New Thread Modal ──────────
function ComposeThreadModal({ mode, clientId, onClose, onCreated }) {
  const [type, setType] = useState('question');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const authMode = mode === 'advisor' ? 'advisor' : 'freelancer';

  const submit = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        thread_type: type,
        subject: subject.trim(),
        body: body.trim(),
        priority,
      };
      // SB always passes client_id; FL leaves it null (server uses own id)
      if (mode === 'advisor') payload.client_id = clientId;
      await axios.post(`${API}/collab/threads`, payload, { headers: getHeaders(authMode) });
      toast.success('Vorgang erstellt');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erstellen fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 px-4" onClick={onClose} data-testid="compose-modal">
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold tracking-tight text-slate-900" style={H}>Neuer Vorgang</h3>
          <p className="mt-1 text-sm text-slate-500">
            {mode === 'advisor' ? 'Stelle deinem Mandanten eine Frage oder weise eine Aufgabe zu.' : 'Stelle deinem Steuerberater eine Frage oder schicke ihm eine Notiz.'}
          </p>

          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Typ</label>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(TYPE_META).map(([k, meta]) => (
                  <button
                    key={k}
                    onClick={() => setType(k)}
                    className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs transition-colors ${
                      type === k ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`type-${k}`}
                  >
                    <meta.Icon className="h-4 w-4" />
                    {meta.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Betreff</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Kurz und prägnant…" data-testid="compose-subject" />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Nachricht</label>
                {mode === 'advisor' && (
                  <TextTemplatePopover
                    currentValue={body}
                    onSelect={(text) => setBody((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))}
                  />
                )}
              </div>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows="4" placeholder="Beschreibe den Vorgang…" data-testid="compose-body" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={priority === 'urgent'}
                onChange={(e) => setPriority(e.target.checked ? 'urgent' : 'normal')}
                className="rounded border-slate-300"
                data-testid="compose-urgent"
              />
              <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
              Als dringend markieren
            </label>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button onClick={submit} disabled={submitting || !subject.trim() || !body.trim()} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="compose-submit">
              {submitting ? '…' : 'Erstellen'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
  return createPortal(modal, document.body);
}
